import React, { Component } from 'react';
import { Heart, Users, Briefcase, Home, UserX, Sparkles, Lock, MessageSquare, Crown, Zap, CheckCircle2, Shield } from 'lucide-react';
import { motion } from 'framer-motion';
export type AnalysisMode = 'default' | 'relationships' | 'ex' | 'friends' | 'family' | 'workplace';
interface ModeOption {
  value: AnalysisMode;
  label: string;
  description: string;
  icon: ComponentType<{
    size?: number;
    className?: string;
  }>;
  isPremium: boolean;
  isGold?: boolean;
}
const modeOptions: ModeOption[] = [{
  value: 'default',
  label: 'Standard Scan',
  description: 'Basic toxicity and manipulation detection',
  icon: Shield,
  isPremium: false
}, {
  value: 'relationships',
  label: 'Romantic Partner',
  description: 'Deep analysis of romantic dynamics',
  icon: Heart,
  isPremium: true,
  isGold: true
}, {
  value: 'ex',
  label: 'Ex-Partner',
  description: 'Detect lingering manipulation or hoovering',
  icon: UserX,
  isPremium: true
}, {
  value: 'friends',
  label: 'Friendship',
  description: 'Identify toxic traits in friendships',
  icon: Users,
  isPremium: true
}, {
  value: 'family',
  label: 'Family Member',
  description: 'Analyze family dynamics and guilt-tripping',
  icon: Home,
  isPremium: true
}, {
  value: 'workplace',
  label: 'Workplace',
  description: 'Detect professional gaslighting or bullying',
  icon: Briefcase,
  isPremium: true
}];
interface ModeSelectorProps {
  selectedMode: AnalysisMode;
  onModeChange: (mode: AnalysisMode) => void;
  disabled?: boolean;
  isPro?: boolean;
  hasCredits?: boolean;
  onUpgradeTrigger?: () => void;
}
export function ModeSelector({
  selectedMode,
  onModeChange,
  disabled = false,
  isPro = false,
  hasCredits = false,
  onUpgradeTrigger
}: ModeSelectorProps) {
  const isUnlocked = (option: ModeOption) => {
    if (!option.isPremium) return true;
    // Gold features might have specific logic in future, for now treating as premium
    return isPro || hasCredits;
  };
  const handleModeClick = (option: ModeOption) => {
    if (disabled) return;
    if (isUnlocked(option)) {
      onModeChange(option.value);
    } else {
      onUpgradeTrigger?.();
    }
  };
  const getCardStyles = (option: ModeOption, isSelected: boolean, unlocked: boolean) => {
    if (isSelected) {
      if (option.isGold) return 'border-amber-400 bg-amber-50 ring-1 ring-amber-400';
      if (option.isPremium) return 'border-red-500 bg-red-50 ring-1 ring-red-500';
      return 'border-gray-400 bg-gray-100 ring-1 ring-gray-400';
    }
    if (unlocked) {
      return 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50';
    }
    return 'border-gray-100 bg-gray-50/50 opacity-70 hover:bg-gray-100';
  };
  const getIconStyles = (option: ModeOption, isSelected: boolean, unlocked: boolean) => {
    if (isSelected) {
      if (option.isGold) return 'bg-amber-100 text-amber-700';
      if (option.isPremium) return 'bg-red-100 text-red-700';
      return 'bg-gray-200 text-gray-800';
    }
    if (unlocked) {
      if (option.isGold) return 'bg-amber-50 text-amber-600 group-hover:bg-amber-100';
      return 'bg-gray-100 text-gray-600 group-hover:bg-gray-200';
    }
    return 'bg-gray-100 text-gray-400';
  };
  return <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm" data-id="element-2983" data-mp="3p6teubzybdv">
      <div className="flex items-center justify-between mb-4" data-id="element-2984" data-mp="3n44yeqhsqb9">
        <div data-id="element-2985" data-mp="7oz6nkcc55pk">
          <label className="block text-sm font-bold text-gray-900 mb-1" data-id="element-2986" data-mp="7opdjnvzuifn">
            Choose Analysis Context
          </label>
          <p className="text-xs text-gray-500" data-id="element-2987" data-mp="hgbxc8medpu0">
            Select the relationship type for more accurate toxicity detection
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-2988" data-mp="mh9h2pn22rxm">
        {modeOptions.map(option => {
        const unlocked = isUnlocked(option);
        const isSelected = selectedMode === option.value;
        const Icon = option.icon;
        return <motion.button key={option.value} type="button" whileHover={!disabled ? {
          scale: 1.01
        } : {}} whileTap={!disabled ? {
          scale: 0.99
        } : {}} onClick={() => handleModeClick(option)} disabled={disabled} className={`
                relative flex items-start gap-3 p-4 rounded-xl border transition-all text-left group
                ${getCardStyles(option, isSelected, unlocked)}
                ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
              `} data-id="element-2989" data-mp="1j7l8nfs2ytto">
              <div className={`
                flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center transition-colors mt-0.5
                ${getIconStyles(option, isSelected, unlocked)}
              `} data-id="element-2990" data-mp="2lkqy82kbi8fm">
                <Icon size={18} data-id="element-2991" data-mp="2j6vjhqff41l8" />
              </div>

              <div className="flex-1 min-w-0" data-id="element-2992" data-mp="2j8geti9k3ok5">
                <div className="flex items-center gap-2 mb-1" data-id="element-2993" data-mp="2o56sjyxw282e">
                  <span className={`
                    font-bold text-sm transition-colors
                    ${isSelected ? 'text-gray-900' : unlocked ? 'text-gray-900' : 'text-gray-500'}
                  `} data-id="element-2994" data-mp="2o7t2244caxn9">
                    {option.label}
                  </span>

                  {option.isGold && <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-100 text-amber-700 border border-amber-200 uppercase tracking-wide" data-id="element-2995" data-mp="373j2vo0jdnzk">
                      🥇 Popular
                    </span>}

                  {!unlocked && <Lock size={12} className="text-gray-400" data-id="element-2996" data-mp="dbzrbvob7t1j" />}
                </div>

                <div className={`text-xs leading-relaxed ${isSelected ? 'text-gray-700' : 'text-gray-500'}`} data-id="element-2997" data-mp="8cdbrfl0e0pk">
                  {option.description}
                </div>
              </div>

              {isSelected && <div className="absolute top-3 right-3" data-id="element-2998" data-mp="3vgksrypaaok0">
                  <CheckCircle2 size={18} className={option.isGold ? 'text-amber-600' : option.isPremium ? 'text-red-600' : 'text-gray-600'} fill="currentColor" color="white" data-id="element-2999" data-mp="3qijdzpyw5bec" />
                </div>}
            </motion.button>;
      })}
      </div>
    </div>;
}